import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Input,
  Label,
  Table,
  Pagination,
  PaginationItem,
  PaginationLink,
  Button,
  Badge,
  InputGroup,
  ButtonGroup,
  Spinner
} from "reactstrap";
import { withTranslation } from "react-i18next";
import UserService from "Api/Services/UserService";
import { useSearchParams } from "react-router-dom";
import debounce from 'lodash/debounce';
import PageTitle from "../../components/Common/PageTitle";
import DoctorSelector from "../../components/Common/DoctorSelector";
import {
  getStoredSelectedDoctorId,
  isGroupLoginUser,
  setStoredSelectedDoctorId,
} from "../../helpers/doctorSelection";

const PATIENT_STATUS_OPTIONS = {
  All: 'All Patients',
  Verification: 'Pending Verification',
  Active: 'In Treatment',
  Complete: 'Treatment Completed',
  Cancelled: 'Cancelled',
};

const normalizeStatusFilter = (status) => (
  status && Object.prototype.hasOwnProperty.call(PATIENT_STATUS_OPTIONS, status)
    ? status
    : status
      ? 'Verification'
      : 'All'
);

const Patients = (props) => {
  document.title = "Patients | Infuse One";

  const [searchParams, setSearchParams] = useSearchParams();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const initialStatusFilter = normalizeStatusFilter(searchParams.get('status'));
  const [statusFilter, setStatusFilter] = useState(initialStatusFilter);
  const [dateFilter, setDateFilter] = useState('');
  const [timeframe, setTimeframe] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sortField, setSortField] = useState('next_appointment_date');
  const [sortDirection, setSortDirection] = useState('asc');
  const [recentAppointments, setRecentAppointments] = useState([]);
  const [loadingAppointments, setLoadingAppointments] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [doctors, setDoctors] = useState([]);
  const [selectedDoctorId, setSelectedDoctorId] = useState(getStoredSelectedDoctorId());
  const [groupLogin] = useState(isGroupLoginUser());
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 10,
    total: 0,
    last_page: 1
  });
  const itemsPerPage = 10;

  const statusOptions = useMemo(() => PATIENT_STATUS_OPTIONS, []);

  const statusBadgeColors = useMemo(() => ({
    Verification: 'warning',
    Active: 'info',
    Complete: 'success',
    Cancelled: 'danger',
    None: 'light'
  }), []);

  const displayPatientStatus = useCallback((status) => (
    status && status !== 'Unknown' ? status : 'Verification'
  ), []);

  const loadAccessibleDoctors = useCallback(async () => {
    try {
      const response = await UserService.GetAccessibleDoctors().json();

      if (!response.status) {
        setError(response.message || "Failed to load doctors");
        return "";
      }

      const availableDoctors = response.data || [];
      const fallbackDoctorId = response.selected_doctor?.id ? String(response.selected_doctor.id) : "";
      const storedDoctorId = getStoredSelectedDoctorId();
      const nextDoctorId = availableDoctors.some(doctor => String(doctor.id) === storedDoctorId)
        ? storedDoctorId
        : fallbackDoctorId;

      setDoctors(availableDoctors);
      setSelectedDoctorId(nextDoctorId);
      setStoredSelectedDoctorId(nextDoctorId);

      return nextDoctorId;
    } catch (loadError) {
      setError("Failed to load doctors");
      return "";
    }
  }, []);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch (error) {
      return dateString;
    }
  }, []);

  const debouncedFetchPatients = useCallback(
    debounce(async (params) => {
      try {
        setIsFetching(true);
        setLoading(true);
        
        const response = await UserService.getPatients({
          page: params.page || 1,
          search: params.search?.trim(),
          status: params.status !== 'All' ? params.status : undefined,
          sortField: params.sortField || 'next_appointment_date',
          sortDirection: params.sortDirection || 'asc',
          perPage: params.perPage || 10,
          doctorId: params.doctorId || undefined,
        });
        
        if (response?.status) {
          setPatients(response.data?.patients || []);
          setPagination({
            current_page: params.page || 1,
            per_page: params.perPage || 10,
            total: response.data?.total || 0,
            last_page: Math.ceil((response.data?.total || 0) / (params.perPage || 10))
          });
          setTotalPages(Math.ceil((response.data?.total || 0) / (params.perPage || 10)));
          setError('');
        } else {
          setError('Failed to load patients');
          setPatients([]);
        }
      } catch (error) {
        setError('Failed to load patients');
        setPatients([]);
      } finally {
        setIsFetching(false);
        setLoading(false);
      }
    }, 150),
    []
  );

  // Initial load
  useEffect(() => {
    const initializePatients = async () => {
      if (searchParams.get('status') !== initialStatusFilter) {
        setSearchParams(initialStatusFilter === 'All' ? {} : { status: initialStatusFilter }, { replace: true });
      }

      if (groupLogin) {
        await loadAccessibleDoctors();
        return;
      }

      debouncedFetchPatients({
        page: 1,
        search: searchTerm,
        status: statusFilter,
        sortField,
        sortDirection,
        perPage: 10
      });
    };

    initializePatients();
  }, [debouncedFetchPatients, groupLogin, loadAccessibleDoctors]);

  useEffect(() => {
    if (!groupLogin || !selectedDoctorId) {
      return;
    }

    setStoredSelectedDoctorId(selectedDoctorId);
    UserService.clearPatientsCache();

    debouncedFetchPatients({
      page: 1,
      search: searchTerm,
      status: statusFilter,
      sortField,
      sortDirection,
      perPage: 10,
      doctorId: selectedDoctorId,
    });
  }, [debouncedFetchPatients, groupLogin, selectedDoctorId]);

  const handleSearch = useCallback((e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setCurrentPage(1);
    
    // Clear cache when search term changes
    UserService.clearPatientsCache();
    
    debouncedFetchPatients({
      page: 1,
      search: value,
      status: statusFilter,
      sortField,
      sortDirection,
      perPage: 10,
      doctorId: groupLogin ? selectedDoctorId : undefined,
    });
  }, [groupLogin, selectedDoctorId, sortField, sortDirection, statusFilter]);

  const handleStatusFilter = useCallback((e) => {
    const newStatus = e.target.value;
    setStatusFilter(newStatus);
    setCurrentPage(1);
    setPatients([]); // Clear current patients when filtering
    setSearchParams(newStatus === 'All' ? {} : { status: newStatus });
    
    // Clear cache when status changes
    UserService.clearPatientsCache();
    
    debouncedFetchPatients({
      page: 1,
      search: searchTerm,
      status: newStatus === 'All' ? undefined : newStatus,
      sortField,
      sortDirection,
      perPage: 10,
      doctorId: groupLogin ? selectedDoctorId : undefined,
    });
  }, [groupLogin, searchTerm, selectedDoctorId, setSearchParams, sortField, sortDirection]);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
    debouncedFetchPatients({
      page,
      search: searchTerm,
      status: statusFilter,
      sortField,
      sortDirection,
      perPage: 10,
      doctorId: groupLogin ? selectedDoctorId : undefined,
    });
  }, [groupLogin, searchTerm, selectedDoctorId, statusFilter, sortDirection, sortField]);

  const handleSort = useCallback((field) => {
    setSortField(field);
    setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    setCurrentPage(1);
    setPatients([]); // Clear current patients when sorting
    
    // Clear cache when sorting changes
    UserService.clearPatientsCache();
    
    debouncedFetchPatients({
      page: 1,
      search: searchTerm,
      status: statusFilter,
      sortField: field,
      sortDirection: sortDirection === 'asc' ? 'desc' : 'asc',
      perPage: 10,
      doctorId: groupLogin ? selectedDoctorId : undefined,
    });
  }, [groupLogin, searchTerm, selectedDoctorId, statusFilter, sortDirection]);

  const handleDateFilter = (e) => {
    setDateFilter(e.target.value);
  };

  const handleTimeframeChange = (value) => {
    setTimeframe(value);
  };

  const fetchRecentAppointments = async (patientId) => {
    setLoadingAppointments(true);
    try {
      const response = await UserService.getAppointments({
        page: 1,
        perPage: 10, // Changed from 5 to 10 to show more appointments
        sort_field: 'appt_date',
        sort_direction: 'desc', // Changed to desc to show most recent first
        patient_id: patientId, // Filter by selected patient
        doctorId: groupLogin ? selectedDoctorId : undefined,
      });

      if (response.status && response.data && response.data.appointments) {
        setRecentAppointments(response.data.appointments);
      }
    } catch (error) {
      setError('Failed to fetch recent appointments');
    } finally {
      setLoadingAppointments(false);
    }
  };

  // Update the setSelectedPatient function to fetch appointments
  const handlePatientSelect = (patient) => {
    setSelectedPatient(patient);
    setSidebarOpen(true);
    fetchRecentAppointments(patient.id);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      debouncedFetchPatients.cancel();
    };
  }, [debouncedFetchPatients]);

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisiblePages = 5;
    const startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    // Always show first page
    pages.push(
      <PaginationItem key={1} active={currentPage === 1}>
        <PaginationLink onClick={() => handlePageChange(1)}>1</PaginationLink>
      </PaginationItem>
    );

    // Show ellipsis if needed
    if (startPage > 2) {
      pages.push(
        <PaginationItem key="start-ellipsis" disabled>
          <PaginationLink>...</PaginationLink>
        </PaginationItem>
      );
    }

    // Show page numbers
    for (let i = startPage; i <= endPage; i++) {
      if (i !== 1 && i !== totalPages) {
        pages.push(
          <PaginationItem key={i} active={currentPage === i}>
            <PaginationLink onClick={() => handlePageChange(i)}>{i}</PaginationLink>
          </PaginationItem>
        );
      }
    }

    // Show ellipsis if needed
    if (endPage < totalPages - 1) {
      pages.push(
        <PaginationItem key="end-ellipsis" disabled>
          <PaginationLink>...</PaginationLink>
        </PaginationItem>
      );
    }

    // Always show last page if there is more than one page
    if (totalPages > 1) {
      pages.push(
        <PaginationItem key={totalPages} active={currentPage === totalPages}>
          <PaginationLink onClick={() => handlePageChange(totalPages)}>
            {totalPages}
          </PaginationLink>
        </PaginationItem>
      );
    }

    return (
      <Pagination className="pagination pagination-rounded justify-content-center mt-4">
        <PaginationItem disabled={currentPage === 1}>
          <PaginationLink previous onClick={() => handlePageChange(currentPage - 1)} />
        </PaginationItem>
        {pages}
        <PaginationItem disabled={currentPage === totalPages}>
          <PaginationLink next onClick={() => handlePageChange(currentPage + 1)} />
        </PaginationItem>
      </Pagination>
    );
  };

  if (error && patients.length === 0) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        </Container>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <div className="container-fluid user-page-shell">
            <PageTitle
              title="Patients"
              actions={groupLogin ? (
                <DoctorSelector
                  doctors={doctors}
                  selectedDoctorId={selectedDoctorId}
                  onChange={setSelectedDoctorId}
                  compact
                />
              ) : null}
            />
            <Row>
              <Col>
                <Card>
                  <CardBody>
                    <Row className="mb-4">
                      <Col md="4">
                        <div className="position-relative">
                          <i className="bx bx-search-alt-2 position-absolute" style={{ 
                            left: '12px', 
                            top: '50%', 
                            transform: 'translateY(-50%)',
                            zIndex: 1,
                            color: '#74788d',
                            fontSize: '16px'
                          }}></i>
                          <Input
                            type="text"
                            className="form-control"
                            placeholder="Search by patient name..."
                            value={searchTerm}
                            onChange={handleSearch}
                            style={{
                              paddingLeft: '35px',
                              height: '38px',
                              border: '1px solid #ced4da',
                              borderRadius: '4px',
                              fontSize: '14px'
                            }}
                          />
                        </div>
                      </Col>
                      <Col md="4">
                        <Input
                          type="select"
                          className="form-select"
                          value={statusFilter}
                          onChange={handleStatusFilter}
                          style={{
                            height: '38px',
                            border: '1px solid #ced4da',
                            borderRadius: '4px',
                            fontSize: '14px'
                          }}
                        >
                          {Object.entries(statusOptions).map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                          ))}
                        </Input>
                      </Col>
                    </Row>

                    <div className="table-responsive">
                      <Table className="table-nowrap align-middle table-hover mb-0">
                        <thead>
                          <tr>
                            <th onClick={() => handleSort('name')} style={{ cursor: 'pointer', padding: '1rem 0.75rem' }}>
                              Patient Name {sortField === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
                            </th>
                            <th onClick={() => handleSort('status')} style={{ cursor: 'pointer', padding: '1rem 0.75rem' }}>
                              Status {sortField === 'status' && (sortDirection === 'asc' ? '↑' : '↓')}
                            </th>
                            <th style={{ padding: '1rem 0.75rem' }}>Insurance Plan</th>
                            <th style={{ padding: '1rem 0.75rem' }}>Home Location</th>
                            <th onClick={() => handleSort('next_appointment_date')} style={{ cursor: 'pointer', padding: '1rem 0.75rem' }}>
                              Next Appointment {sortField === 'next_appointment_date' && (sortDirection === 'asc' ? '↑' : '↓')}
                            </th>
                            <th onClick={() => handleSort('last_appointment_date')} style={{ cursor: 'pointer', padding: '1rem 0.75rem' }}>
                              Last Treatment {sortField === 'last_appointment_date' && (sortDirection === 'asc' ? '↑' : '↓')}
                            </th>
                            <th style={{ padding: '1rem 0.75rem' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {isFetching ? (
                            <tr>
                              <td colSpan="7" className="text-center" style={{ padding: '1rem 0.75rem' }}>
                                <div className="d-flex align-items-center justify-content-center">
                                  <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                  </div>
                                  Searching patients...
                                </div>
                              </td>
                            </tr>
                          ) : patients.length > 0 ? (
                            patients.map((patient) => (
                              <tr key={patient.id}>
                                <td style={{ padding: '1rem 0.75rem' }}>{patient.name || '-'}</td>
                                <td style={{ padding: '1rem 0.75rem' }}>
                                  <Badge color={statusBadgeColors[displayPatientStatus(patient.status)] || 'secondary'} className="status-badge">
                                    {displayPatientStatus(patient.status)}
                                  </Badge>
                                </td>
                                <td style={{ padding: '1rem 0.75rem' }}>{patient.insurance_plan || 'Not Specified'}</td>
                                <td style={{ padding: '1rem 0.75rem' }}>{patient.home_location || 'Not Specified'}</td>
                                <td style={{ padding: '1rem 0.75rem' }}>{formatDate(patient.next_appointment_date)}</td>
                                <td style={{ padding: '1rem 0.75rem' }}>{formatDate(patient.last_appointment_date)}</td>
                                <td style={{ padding: '1rem 0.75rem' }}>
                                  <Button
                                    className="btn-primary"
                                    size="sm"
                                    onClick={() => handlePatientSelect(patient)}
                                  >
                                    View Details
                                  </Button>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="7" className="text-center" style={{ padding: '1rem 0.75rem' }}>
                                No patients found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </div>

                    {totalPages > 1 && renderPagination()}
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </div>
        </Container>
      </div>
      {/* Patient Details Sidebar */}
      <div className={`patient-details-sidebar ${sidebarOpen ? 'show' : ''}`} style={{
        position: 'fixed',
        top: 0,
        right: 0,
        width: '550px',
        height: '100vh',
        backgroundColor: '#fff',
        boxShadow: '-5px 0 15px rgba(0, 0, 0, 0.1)',
        zIndex: 1050,
        padding: '20px',
        transform: sidebarOpen ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.3s ease-in-out',
        overflowY: 'auto'
      }}>
        <div className="d-flex justify-content-between align-items-center mb-4" style={{ padding: '1.25rem 0 1.25rem 0.75rem' }}>
          <h4 className="mb-0" style={{ fontSize: '1.25rem', fontWeight: '500' }}>Patient Details</h4>
          <Button color="link" className="p-0" onClick={() => setSidebarOpen(false)} style={{ 
            fontSize: '2rem', 
            color: '#74788d',
            padding: '0.75rem',
            marginRight: '0.5rem',
            opacity: '0.9'
          }}>
            <i className="mdi mdi-close"></i>
          </Button>
        </div>
        
        {selectedPatient && (
          <div>
            <Card className="mb-3" style={{ marginTop: '-1rem' }}>
              <CardBody style={{ paddingTop: '0.75rem' }}>
                <h3 className="card-title mb-2" style={{ fontSize: '1.35rem', fontWeight: '600' }}>{selectedPatient.name}</h3>
                <div className="mt-2">
                  <Badge 
                    color={statusBadgeColors[displayPatientStatus(selectedPatient.status)] || 'secondary'}
                    className="me-1"
                    style={{ 
                      fontSize: '0.9rem',
                      padding: '0.4rem 0.8rem',
                      fontWeight: '500'
                    }}
                  >
                    {displayPatientStatus(selectedPatient.status)}
                  </Badge>
                </div>
              </CardBody>
            </Card>
            
            <Card className="mb-3">
              <CardBody>
                <h5 className="card-title mb-3">Patient Information</h5>
                <div className="patient-info">
                  <div className="d-flex mb-2">
                    <div className="text-muted" style={{width: '40%', fontSize: '0.95rem'}}>Patient ID:</div>
                    <div style={{width: '60%', fontSize: '0.95rem'}}>{selectedPatient.id}</div>
                  </div>
                  <div className="d-flex mb-2">
                    <div className="text-muted" style={{width: '40%', fontSize: '0.95rem'}}>Insurance Plan:</div>
                    <div style={{width: '60%', fontSize: '0.95rem'}}>{selectedPatient.insurance_plan || 'Not Specified'}</div>
                  </div>
                  <div className="d-flex mb-2">
                    <div className="text-muted" style={{width: '40%', fontSize: '0.95rem'}}>Home Location:</div>
                    <div style={{width: '60%', fontSize: '0.95rem'}}>{selectedPatient.home_location || 'Not Specified'}</div>
                  </div>
                  <div className="d-flex mb-2">
                    <div className="text-muted" style={{width: '40%', fontSize: '0.95rem'}}>Next Appointment:</div>
                    <div style={{width: '60%', fontSize: '0.95rem'}}>{formatDate(selectedPatient.next_appointment_date)}</div>
                  </div>
                  <div className="d-flex mb-2">
                    <div className="text-muted" style={{width: '40%', fontSize: '0.95rem'}}>Last Treatment:</div>
                    <div style={{width: '60%', fontSize: '0.95rem'}}>{formatDate(selectedPatient.last_appointment_date)}</div>
                  </div>
                </div>
              </CardBody>
            </Card>
            
            <Card className="mb-3">
              <CardBody>
                <h5 className="card-title mb-3">Recent Appointments</h5>
                <div className="table-responsive">
                  <Table className="table-centered table-nowrap mb-0">
                    <thead>
                      <tr>
                        <th>Appointment Date</th>
                        <th>Status</th>
                        <th>Location</th>
                        <th>Drug</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loadingAppointments ? (
                        <tr>
                          <td colSpan="4" className="text-center">
                            <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
                              <span className="visually-hidden">Loading...</span>
                            </div>
                            Loading appointments...
                          </td>
                        </tr>
                      ) : recentAppointments.length > 0 ? (
                        recentAppointments.map((appointment) => (
                          <tr key={appointment.id}>
                            <td>{appointment.appt_date ? (() => {
                              const date = new Date(appointment.appt_date);
                              const weekday = date.toLocaleString(undefined, { weekday: 'short' });
                              const month = date.toLocaleString(undefined, { month: 'long' });
                              const day = date.getDate();
                              const year = date.getFullYear();
                              return `${weekday}. ${month} ${day}, ${year}`;
                            })() : 'N/A'}</td>
                            <td>
                              <Badge
                                color={
                                  appointment.appt_status?.toLowerCase() === "active" ? "info" :
                                  appointment.appt_status?.toLowerCase() === "complete" || appointment.appt_status?.toLowerCase() === "completed" ? "success" :
                                  appointment.appt_status?.toLowerCase() === "scheduled" ? "warning" :
                                  appointment.appt_status?.toLowerCase() === "canceled" || appointment.appt_status?.toLowerCase() === "cancelled" ? "danger" :
                                  "secondary"
                                }
                                className="status-badge"
                              >
                                {appointment.appt_status ? appointment.appt_status.charAt(0).toUpperCase() + appointment.appt_status.slice(1).toLowerCase() : 'Unknown'}
                              </Badge>
                            </td>
                            <td>{appointment.location_name || 'N/A'}</td>
                            <td>{appointment.prescription || 'N/A'}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="4" className="text-center">
                            No recent appointments found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </CardBody>
            </Card>
          </div>
        )}
      </div>
      
      {/* Overlay when sidebar is open */}
      {sidebarOpen && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1040
          }}
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </React.Fragment>
  );
};

export default withTranslation()(Patients); 
